/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonVariety, Link, LinkHighlight, Text } from '@sonarsource/echoes-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Path } from 'react-router-dom';
import { useJiraUserActionsQuery } from '../../queries';
import { JiraPermission } from '../../types';
import JiraProjectBindingDialog from './JiraProjectBindingDialog';

interface JiraProjectConnectButtonProps {
  hasError: boolean;
  isOrgAdmin: boolean;
  isOrganizationBound: boolean;
  orgBindingId?: string;
  orgBindingUrl: Partial<Path>;
  orgId?: string;
  shouldCheckForBindingPermissions?: boolean;
  sonarProjectId: string;
  sonarProjectName: string;
}

export default function JiraProjectConnectButton({
  hasError,
  isOrgAdmin,
  isOrganizationBound,
  orgBindingId,
  orgBindingUrl,
  orgId,
  sonarProjectId,
  sonarProjectName,
  shouldCheckForBindingPermissions,
}: Readonly<JiraProjectConnectButtonProps>) {
  const [projectBindingDialogOpen, setProjectBindingDialogOpen] = useState(false);
  const {
    // If check for permissions is not needed, user can bind project (SQC behavior)
    data: canBindProject = !shouldCheckForBindingPermissions,
    isLoading: isLoadingUserActions,
  } = useJiraUserActionsQuery(sonarProjectId, {
    select: (actions) => actions.includes(JiraPermission.BIND_PROJECT),
    enabled: shouldCheckForBindingPermissions === true,
  });
  const connectIsDisabled =
    !isOrganizationBound || hasError || isLoadingUserActions || !canBindProject;

  const getHelperText = () => {
    if (!isOrganizationBound && !hasError) {
      return (
        <Text className="sw-ml-2" isSubtle>
          <FormattedMessage
            id="project_settings.jira_binding.organization_not_bound"
            values={{
              link: (text) =>
                isOrgAdmin ? (
                  <Link highlight={LinkHighlight.CurrentColor} to={orgBindingUrl}>
                    {text}
                  </Link>
                ) : (
                  text
                ),
            }}
          />
        </Text>
      );
    }

    if (!isLoadingUserActions && !canBindProject) {
      return (
        <Text className="sw-ml-2" isSubtle>
          <FormattedMessage id="project_settings.jira_binding.no_permissions" />
        </Text>
      );
    }

    return null;
  };

  return (
    <>
      <div className="sw-flex sw-items-center">
        <Button
          isDisabled={connectIsDisabled}
          isLoading={isLoadingUserActions}
          onClick={() => {
            setProjectBindingDialogOpen(true);
          }}
          variety={ButtonVariety.Primary}
        >
          <FormattedMessage id="connect" />
        </Button>

        {getHelperText()}
      </div>

      <JiraProjectBindingDialog
        isOpen={projectBindingDialogOpen}
        onClose={() => {
          setProjectBindingDialogOpen(false);
        }}
        orgBindingId={orgBindingId}
        orgId={orgId}
        sonarProjectId={sonarProjectId}
        sonarProjectName={sonarProjectName}
      />
    </>
  );
}
